export type GoogleSpeakerSlot = 'A' | 'B';

export type GoogleTtsModel = 'gemini-2.5-pro-tts' | 'gemini-2.5-flash-tts';

export interface GoogleTtsModelOption {
  value: GoogleTtsModel;
  label: string;
}

export const GOOGLE_TTS_MODELS = {
  best: 'gemini-2.5-pro-tts',
  fast: 'gemini-2.5-flash-tts',
} as const;

export const GOOGLE_TTS_MODEL_OPTIONS: GoogleTtsModelOption[] = [
  { value: GOOGLE_TTS_MODELS.best, label: 'Best' },
  { value: GOOGLE_TTS_MODELS.fast, label: 'Fast' },
];

export const GOOGLE_TTS_MODEL = GOOGLE_TTS_MODELS.best;

export const isGoogleTtsModel = (value: unknown): value is GoogleTtsModel => value === GOOGLE_TTS_MODELS.best || value === GOOGLE_TTS_MODELS.fast;

export interface GoogleSpeakerConfig {
  voiceId: string;
  displayName: string;
}

export interface GoogleAiVoiceSegment {
  id: string;
  speaker: GoogleSpeakerSlot;
  text: string;
}

export interface GoogleAiVoiceDraftV1 {
  version: 1;
  provider: 'google';
  model: GoogleTtsModel;
  languageCode: string;
  multiSpeaker?: boolean;
  promptSpeakerA?: string;
  promptSpeakerB?: string;
  speakers: {
    speakerA: GoogleSpeakerConfig;
    speakerB: GoogleSpeakerConfig;
  };
  segments: GoogleAiVoiceSegment[];
  prompt?: string;
}

export interface GoogleVoiceOption {
  voiceId: string;
  displayName: string;
  languageCode: string;
  gender: string;
}

export interface LanguageOption {
  code: string;
  label: string;
}

export interface GoogleLanguageApiOption {
  languageCode?: string;
  displayName?: string;
}

export interface GoogleVoiceCatalogApiResponse {
  model?: string;
  languages?: GoogleLanguageApiOption[];
  voices?: GoogleVoiceOption[];
}

export interface GoogleSynthesisRequest {
  script: {
    model: GoogleTtsModel;
    languageCode: string;
    multiSpeaker?: boolean;
    speakers: {
      speakerA: {
        voiceId: string;
        speakerAlias?: string;
      };
      speakerB?: {
        voiceId: string;
        speakerAlias?: string;
      };
    };
    segments: Array<{
      speaker: GoogleSpeakerSlot;
      text: string;
    }>;
    prompt?: string;
  };
  audioConfig?: {
    audioEncoding?: 'MP3' | 'LINEAR16';
    sampleRateHertz?: number;
  };
}
export const GOOGLE_DEFAULT_LANGUAGE = '' as const;
export const GOOGLE_DEFAULT_PROMPT = 'Read aloud in a warm, welcoming tone.' as const;

export const createGoogleSegmentId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `seg-${crypto.randomUUID()}`;
  }
  return `seg-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;
};

const readStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const parseStoredSpeaker = (value: string | null, fallback: GoogleSpeakerConfig): GoogleSpeakerConfig => {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value) as Partial<GoogleSpeakerConfig>;
    const voiceId = String(parsed?.voiceId ?? '').trim();
    const displayName = String(parsed?.displayName ?? voiceId).trim();
    if (!voiceId) return fallback;
    return { voiceId, displayName: displayName || voiceId };
  } catch {
    return fallback;
  }
};

const getDefaultLanguageCode = () => {
  return readStorageItem('languageCode') ?? GOOGLE_DEFAULT_LANGUAGE;
};

const getDefaultVoiceA = () => {
  return parseStoredSpeaker(readStorageItem('voiceSpeakerA'), { voiceId: 'Achernar', displayName: 'Achernar' });
};

const getDefaultVoiceB = () => {
  return parseStoredSpeaker(readStorageItem('voiceSpeakerB'), { voiceId: 'Achird', displayName: 'Achird' });
};

export const createDefaultGoogleDraft = (): GoogleAiVoiceDraftV1 => ({
  version: 1,
  provider: 'google',
  model: GOOGLE_TTS_MODEL,
  languageCode: getDefaultLanguageCode(),
  multiSpeaker: false,
  speakers: {
    speakerA: getDefaultVoiceA(),
    speakerB: getDefaultVoiceB(),
  },
  promptSpeakerA: '',
  promptSpeakerB: '',
  segments: [{ id: createGoogleSegmentId(), speaker: 'A', text: '' }],
  prompt: GOOGLE_DEFAULT_PROMPT,
});

export const isGoogleAiVoiceDraft = (value: unknown): value is GoogleAiVoiceDraftV1 => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<GoogleAiVoiceDraftV1>;
  if (candidate.provider !== 'google' || candidate.version !== 1 || !isGoogleTtsModel(candidate.model)) return false;
  if (!candidate.speakers || !candidate.segments || !Array.isArray(candidate.segments)) return false;
  if (!candidate.speakers.speakerA || !candidate.speakers.speakerB) return false;
  return true;
};
